import React, { useMemo } from 'react';

import { useCollection } from '@amzn/awsui-collection-hooks';
import Table from '@amzn/awsui-components-console/table';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import TextFilter from '@amzn/awsui-components-console/text-filter';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Pagination from '@amzn/awsui-components-console/pagination';

import { StringGroupItem } from 'src/generated/graphql';
import { getStringGroupColumns } from 'src/features/namespace/columns';
import { STRING_GROUPS_SEARCH_MIN_CHARS } from 'src/features/namespace/constants';
import {
  TablePreferences,
  useTablePreferences,
} from 'src/common/ui/TablePreferences';
import { stringGroupsPreferencesConfig } from './stringGroupsTablePreferences';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface StringGroupsTableProps {
  /** The namespace ID used to construct string group detail links. */
  namespaceId: string;
  /** All loaded items for the current mode (browse or search). */
  displayItems: StringGroupItem[];
  /** True during initial data load. */
  loading: boolean;
  /** Whether a Load More request is currently in flight. */
  isLoadingMore: boolean;
  /** Whether more items are available to load. */
  hasMore: boolean;
  /** Fetch the next batch of items. */
  handleLoadMore: () => void;
  /** When true, hide the CX Family ID column (namespace already shows it). */
  hideCxFamilyColumn?: boolean;
}

/** Displays a table of string groups with client-side filtering, pagination,
 * and sorting, plus a Load More button to fetch additional items from the
 * server. Filtering happens entirely on the already-loaded `displayItems` via
 * useCollection — no server round-trip on keystroke. */
const StringGroupsTable: React.FC<StringGroupsTableProps> = ({
  namespaceId,
  displayItems,
  loading,
  isLoadingMore,
  hasMore,
  handleLoadMore,
  hideCxFamilyColumn,
}) => {
  const { t, locale } = useTranslation();
  const columns = useMemo(
    () =>
      hideCxFamilyColumn
        ? getStringGroupColumns(t, locale, namespaceId).filter(
            (col) => col.id !== 'cxFamilyId',
          )
        : getStringGroupColumns(t, locale, namespaceId),
    // `t` is a dependency: the labels are localized, so a stale `t` would keep the
    // previous language's wording after a locale change.
    [t, locale, hideCxFamilyColumn, namespaceId],
  );

  // Memoised: a fresh config each render would re-create the confirm handler.
  const preferencesConfig = useMemo(
    () => stringGroupsPreferencesConfig(t, hideCxFamilyColumn === true),
    // `t` is a dependency: the config resolves localized column labels.
    [t, hideCxFamilyColumn],
  );
  // `useCollection` only CLAMPS the page index on a size change, so reset it here.
  // `actions` is read lazily: it comes from the call below, which needs this size.
  const { preferences, onConfirm, columnDisplay } = useTablePreferences(
    preferencesConfig,
    { onPageSizeChange: () => actions.setCurrentPage(1) },
  );

  const {
    items,
    paginationProps,
    collectionProps,
    filterProps,
    filteredItemsCount,
    actions,
  } = useCollection(displayItems, {
    filtering: {
      // cxFamilyId and updatedBy live on the nested latestVersion object;
      // useCollection's default filteringFunction does not traverse nested
      // objects, so every searchable field is read explicitly here.
      filteringFunction: (item: StringGroupItem, filteringText: string) => {
        const text = filteringText.trim().toLowerCase();
        // Below the threshold the filter is a no-op so every loaded row stays
        // visible, rather than collapsing the table on a 1-2 char substring.
        if (text.length < STRING_GROUPS_SEARCH_MIN_CHARS) {
          return true;
        }
        const fields = [
          item.stringGroupId,
          item.teamName,
          item.createdBy,
          item.latestVersion?.cxFamilyId,
          item.latestVersion?.updatedBy,
        ];
        item.latestVersion?.cxIds.forEach((cxId) => {
          fields.push(cxId);
        });
        return fields.some((field) =>
          (field ?? '').toLowerCase().includes(text),
        );
      },
    },
    // Always a number: the config declares page sizes.
    pagination: { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: 'stringGroupId' },
        isDescending: false,
      },
    },
  });

  // With client-side filtering, `displayItems.length` alone would stay at the
  // total while the visible rows shrink. Show `matched/total` only once the
  // filter is actually applied so the header matches what the user sees.
  const trimmedFilter = (filterProps.filteringText ?? '').trim();
  const isFilterApplied =
    trimmedFilter.length >= STRING_GROUPS_SEARCH_MIN_CHARS;
  const showMinCharsHint =
    trimmedFilter.length > 0 &&
    trimmedFilter.length < STRING_GROUPS_SEARCH_MIN_CHARS;
  const headerCount = isFilterApplied
    ? `${filteredItemsCount}/${displayItems.length}`
    : `${displayItems.length}`;

  return (
    <Table<StringGroupItem>
      {...collectionProps}
      variant="container"
      trackBy="stringGroupId"
      ariaLabels={{ tableLabel: t('string-groups-table-aria') }}
      stickyHeader={true}
      stickyColumns={{ first: 1 }}
      loading={loading}
      wrapLines={preferences.wrapLines}
      stripedRows={preferences.stripedRows}
      contentDensity={preferences.contentDensity}
      resizableColumns
      header={
        <Header variant="awsui-h1-sticky">
          {t('string-groups-header', { count: headerCount })}
        </Header>
      }
      filter={
        <>
          <TextFilter
            {...filterProps}
            filteringPlaceholder={t('string-groups-filter-placeholder')}
            filteringAriaLabel={t('string-groups-filter-aria')}
          />
          {/*
            Always-present live region: the node stays mounted so NVDA/JAWS
            observe the text change when the hint appears, rather than the text
            being present at insertion time (frequently dropped).
          */}
          <span role="status">
            {showMinCharsHint ? (
              <Box variant="small" color="text-status-error">
                {t('string-groups-min-chars-hint', {
                  min: STRING_GROUPS_SEARCH_MIN_CHARS,
                })}
              </Box>
            ) : (
              ''
            )}
          </span>
        </>
      }
      pagination={
        <SpaceBetween direction="horizontal" size="xs">
          <Pagination {...paginationProps} />
          {hasMore && (
            <Button
              variant="primary"
              loading={isLoadingMore}
              disabled={isLoadingMore}
              onClick={handleLoadMore}
            >
              {t('string-groups-load-more')}
            </Button>
          )}
        </SpaceBetween>
      }
      columnDefinitions={columns}
      columnDisplay={columnDisplay}
      preferences={
        <TablePreferences
          config={preferencesConfig}
          preferences={preferences}
          onConfirm={onConfirm}
        />
      }
      items={items}
      loadingText={t('string-groups-loading')}
      skeleton={loading ? { totalRows: 6 } : undefined}
      empty={
        <Box textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <Box variant="strong">{t('string-groups-empty-title')}</Box>
            <Box variant="p" color="inherit">
              {t('string-groups-empty-body')}
            </Box>
          </SpaceBetween>
        </Box>
      }
    />
  );
};

export default StringGroupsTable;
